import { useEffect, useMemo, useRef, useState } from "react";
import { AttributeSelector, SoulChainSelector } from "./components/AttributeSelector";
import { ExpressionInput } from "./components/ExpressionInput";
import { FieldSection } from "./components/FieldSection";
import { calculateDamage, BASE_CRITICAL_MULTIPLIER } from "./lib/damage";
import { parseExpression, parsePercentageExpression } from "./lib/expression";
import { formatCompactNumber } from "./lib/format";
import {
  getAttackSoulOptions,
  getDefenseSoulOptions,
  resolveRestraint,
} from "./lib/restraint";
import type { Attribute, DamageResult, RestraintBreakdown, SoulChainSelection } from "./types";

const ATTACK_A_FIELDS = [
  ["attackA_talent", "天赋"],
  ["attackA_skill", "技能"],
  ["attackA_weapon", "兵刃"],
  ["attackA_divineWeapon", "神兵"],
  ["attackA_starSoul", "星魂"],
  ["attackA_accessory", "饰品"],
  ["attackA_soulStone", "魂石·同魂"],
  ["attackA_buff", "Buff"],
  ["attackA_formation", "战阵"],
  ["attackA_assist", "援袭·共势"],
] as const;

const ATTACK_B_FIELDS = [
  ["attackB_astrolabe", "星盘"],
  ["attackB_starlight", "星曜"],
  ["attackB_soulEntry", "魂石词条"],
] as const;

const DEFENSE_A_FIELDS = [
  ["defenseA_talent", "天赋"],
  ["defenseA_skill", "技能"],
  ["defenseA_weapon", "兵刃/及身"],
  ["defenseA_divineWeapon", "神兵"],
  ["defenseA_starSoul", "星魂"],
  ["defenseA_accessory", "饰品"],
  ["defenseA_soulStone", "魂石·同魂"],
  ["defenseA_buff", "Buff"],
  ["defenseA_formation", "战阵"],
  ["defenseA_assist", "援袭·共势"],
] as const;

const DEFENSE_B_FIELDS = [
  ["defenseB_starlight", "星曜"],
  ["defenseB_soulEntry", "魂石词条"],
] as const;

const ALL_PERCENT_KEYS = [
  "penetration",
  "manualExtraRestraint",
  "cDamageIncrease",
  "extraCriticalDamage",
  "manualRestraintReduction",
  "dDamageReduction",
  "criticalDamageReduction",
  ...ATTACK_A_FIELDS.map(([key]) => key),
  ...ATTACK_B_FIELDS.map(([key]) => key),
  ...DEFENSE_A_FIELDS.map(([key]) => key),
  ...DEFENSE_B_FIELDS.map(([key]) => key),
];

const PERCENT_KEY_SET = new Set<string>(ALL_PERCENT_KEYS);

const createInitialFields = (): Record<string, string> => {
  const initial: Record<string, string> = {
    attack: "",
    defense: "",
    penetration: "",
    skillMultiplier: "1",
    manualExtraRestraint: "",
    cDamageIncrease: "",
    extraCriticalDamage: "",
    manualRestraintReduction: "",
    dDamageReduction: "",
    criticalDamageReduction: "",
  };
  for (const key of ALL_PERCENT_KEYS) initial[key] ??= "";
  return initial;
};

function formatSignedPercent(value: number): string {
  if (Math.abs(value) < 1e-10) return "0%";
  const sign = value > 0 ? "+" : "−";
  return `${sign}${Number((Math.abs(value) * 100).toFixed(8))}%`;
}

export default function App() {
  const [fields, setFields] = useState<Record<string, string>>(createInitialFields);
  const [attacker, setAttacker] = useState<Attribute>("无");
  const [defender, setDefender] = useState<Attribute>("无");
  const [attackSoulChain, setAttackSoulChain] = useState<SoulChainSelection>(null);
  const [defenseSoulChain, setDefenseSoulChain] = useState<SoulChainSelection>(null);
  const [showWarning, setShowWarning] = useState(false);
  const [resetEpoch, setResetEpoch] = useState(0);
  const wasNegative = useRef(false);

  const parsedFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).map(([key, raw]) => [
          key,
          PERCENT_KEY_SET.has(key) ? parsePercentageExpression(raw) : parseExpression(raw),
        ]),
      ),
    [fields],
  );

  const valueOf = (key: string) => parsedFields[key]?.value ?? 0;
  const allFieldsValid = Object.values(parsedFields).every((result) => result.ok);

  const restraint = useMemo<RestraintBreakdown>(
    () =>
      resolveRestraint({
        attacker,
        defender,
        attackSoulChain,
        defenseSoulChain,
        manualExtra: valueOf("manualExtraRestraint"),
        manualReduction: valueOf("manualRestraintReduction"),
      }),
    [
      attacker,
      defender,
      attackSoulChain,
      defenseSoulChain,
      parsedFields.manualExtraRestraint?.value,
      parsedFields.manualRestraintReduction?.value,
    ],
  );

  const damage = useMemo<DamageResult | null>(() => {
    if (!allFieldsValid) return null;
    return calculateDamage(
      {
        attack: valueOf("attack"),
        defense: valueOf("defense"),
        penetration: valueOf("penetration"),
        skillMultiplier: valueOf("skillMultiplier"),
        aDamageIncrease: ATTACK_A_FIELDS.map(([key]) => valueOf(key)),
        bDamageIncrease: ATTACK_B_FIELDS.map(([key]) => valueOf(key)),
        cDamageIncrease: valueOf("cDamageIncrease"),
        aDamageReduction: DEFENSE_A_FIELDS.map(([key]) => valueOf(key)),
        bDamageReduction: DEFENSE_B_FIELDS.map(([key]) => valueOf(key)),
        dDamageReduction: valueOf("dDamageReduction"),
        extraCriticalDamage: valueOf("extraCriticalDamage"),
        criticalDamageReduction: valueOf("criticalDamageReduction"),
      },
      restraint,
    );
  }, [allFieldsValid, parsedFields, restraint]);

  useEffect(() => {
    const negative = damage !== null && (damage.nonCritical < 0 || damage.critical < 0);
    if (!negative) {
      wasNegative.current = false;
      setShowWarning(false);
      return;
    }
    if (wasNegative.current) return;
    const timer = window.setTimeout(() => {
      wasNegative.current = true;
      setShowWarning(true);
    }, 450);
    return () => window.clearTimeout(timer);
  }, [damage?.nonCritical, damage?.critical]);

  const setField = (key: string, value: string) => {
    setFields((current) => ({ ...current, [key]: value }));
  };

  const handleAttackerChange = (attribute: Attribute) => {
    setAttacker(attribute);
    const options = getAttackSoulOptions(attribute);
    if (attackSoulChain === null || !options.includes(attackSoulChain)) setAttackSoulChain(null);
  };

  const handleDefenderChange = (attribute: Attribute) => {
    setDefender(attribute);
    const options = getDefenseSoulOptions(attribute);
    if (defenseSoulChain === null || !options.includes(defenseSoulChain)) setDefenseSoulChain(null);
  };

  const reset = () => {
    setFields(createInitialFields());
    setAttacker("无");
    setDefender("无");
    setAttackSoulChain(null);
    setDefenseSoulChain(null);
    setShowWarning(false);
    setResetEpoch((current) => current + 1);
    wasNegative.current = false;
  };

  const attackATotal = ATTACK_A_FIELDS.reduce((total, [key]) => total + valueOf(key), 0);
  const attackBTotal = ATTACK_B_FIELDS.reduce((total, [key]) => total + valueOf(key), 0);
  const defenseATotal = DEFENSE_A_FIELDS.reduce((total, [key]) => total + valueOf(key), 0);
  const defenseBTotal = DEFENSE_B_FIELDS.reduce((total, [key]) => total + valueOf(key), 0);
  const criticalMultiplier =
    BASE_CRITICAL_MULTIPLIER + valueOf("extraCriticalDamage") - valueOf("criticalDamageReduction");
  const manualRestraintNet =
    valueOf("manualExtraRestraint") - valueOf("manualRestraintReduction");
  const manualRestraintValid =
    parsedFields.manualExtraRestraint?.ok && parsedFields.manualRestraintReduction?.ok;

  return (
    <div className="app">
      <header className="hero">
        <div className="hero-shade" />
        <div className="hero-content">
          <p className="eyebrow">天地有法 · 战局可算</p>
          <h1>天地劫伤害计算器</h1>
          <p>属相克制、魂链与增减伤乘区，全部在本机即时计算</p>
          <button type="button" className="reset-button" onClick={reset}>
            重置全部参数
          </button>
        </div>
      </header>

      <main className="page-content">
        <section className="matchup-card" aria-labelledby="matchup-title">
          <div className="section-heading">
            <div>
              <span>属相判定</span>
              <h2 id="matchup-title">攻守关系</h2>
            </div>
            <div className={`restraint-badge ${restraint.tone}`}>{restraint.label}</div>
          </div>

          <div className="matchup-layout">
            <div className="attribute-side">
              <AttributeSelector
                label="进攻方属相"
                side="attack"
                value={attacker}
                onChange={handleAttackerChange}
              />
              <SoulChainSelector
                label="进攻魂链"
                options={getAttackSoulOptions(attacker)}
                value={attackSoulChain}
                onChange={setAttackSoulChain}
              />
            </div>

            <div className="restraint-summary" aria-label="克制系数汇总">
              <span className="duel-mark">攻</span>
              <div className="coefficient-total">
                <small>最终克制乘区</small>
                <strong>{formatCompactNumber(restraint.effectiveMultiplier)}</strong>
              </div>
              <span className="duel-mark defense">守</span>
              <dl>
                <div><dt>基础属相</dt><dd>{formatSignedPercent(restraint.baseModifier)}</dd></div>
                <div><dt>进攻魂链</dt><dd>{formatSignedPercent(restraint.attackSoulBonus)}</dd></div>
                {Math.abs(restraint.manualExtra) > 1e-10 && (
                  <div><dt>额外克制</dt><dd>{formatSignedPercent(restraint.manualExtra)}</dd></div>
                )}
                {Math.abs(restraint.manualReduction) > 1e-10 && (
                  <div><dt>克制减免</dt><dd>{formatSignedPercent(-restraint.manualReduction)}</dd></div>
                )}
                <div><dt>防守魂链</dt><dd>{formatSignedPercent(-restraint.defenseSoulReduction)}</dd></div>
              </dl>
            </div>

            <div className="attribute-side">
              <AttributeSelector
                label="防守方属相"
                side="defense"
                value={defender}
                onChange={handleDefenderChange}
              />
              <SoulChainSelector
                label="防守魂链"
                options={getDefenseSoulOptions(defender)}
                value={defenseSoulChain}
                onChange={setDefenseSoulChain}
              />
            </div>
          </div>

          <details className="restraint-adjustments" key={`restraint-${resetEpoch}`}>
            <summary>
              <span>手动克制修正</span>
              <strong className={Math.abs(manualRestraintNet) > 1e-10 ? "active" : ""}>
                {!manualRestraintValid
                  ? "公式有误"
                  : Math.abs(manualRestraintNet) > 1e-10
                    ? `净修正 ${formatSignedPercent(manualRestraintNet)}`
                    : "未设置"}
              </strong>
            </summary>
            <div className="restraint-adjustment-grid">
              <div className="restraint-adjustment attack">
                <ExpressionInput
                  label="额外克制系数加成"
                  value={fields.manualExtraRestraint}
                  onChange={(value) => setField("manualExtraRestraint", value)}
                  percentage
                />
              </div>
              <div className="restraint-adjustment defense">
                <ExpressionInput
                  label="克制系数减免"
                  value={fields.manualRestraintReduction}
                  onChange={(value) => setField("manualRestraintReduction", value)}
                  percentage
                />
              </div>
            </div>
          </details>
        </section>

        <div className="calculator-columns" key={`calculator-${resetEpoch}`}>
          <section className="side-card attack-card" aria-labelledby="attack-title">
            <div className="side-card-title">
              <span>攻</span>
              <div><small>ATTACKER</small><h2 id="attack-title">进攻方参数</h2></div>
            </div>

            <div className="base-fields attack-base-fields">
              <ExpressionInput
                label="物攻 / 法攻"
                value={fields.attack}
                onChange={(value) => setField("attack", value)}
                placeholder="例如 2916*(1+0.2+0.5)"
                hint="必填，可直接输入面板计算式"
              />
              <ExpressionInput
                label="物穿 / 法穿"
                value={fields.penetration}
                onChange={(value) => setField("penetration", value)}
                percentage
              />
              <ExpressionInput
                label="技能倍率"
                value={fields.skillMultiplier}
                onChange={(value) => setField("skillMultiplier", value)}
                hint="1代表100%倍率"
              />
            </div>

            <FieldSection title="A类增伤" badge={formatSignedPercent(attackATotal)} tone="attack">
              {ATTACK_A_FIELDS.map(([key, label]) => (
                <ExpressionInput
                  key={key}
                  label={label}
                  value={fields[key]}
                  onChange={(value) => setField(key, value)}
                  percentage
                />
              ))}
            </FieldSection>

            <FieldSection title="B类增伤" badge={formatSignedPercent(attackBTotal)} tone="attack">
              {ATTACK_B_FIELDS.map(([key, label]) => (
                <ExpressionInput
                  key={key}
                  label={label}
                  value={fields[key]}
                  onChange={(value) => setField(key, value)}
                  percentage
                />
              ))}
            </FieldSection>

            <FieldSection title="C类增伤" badge={formatSignedPercent(valueOf("cDamageIncrease"))} tone="attack">
              <ExpressionInput
                label="C类增伤"
                value={fields.cDamageIncrease}
                onChange={(value) => setField("cDamageIncrease", value)}
                percentage
              />
            </FieldSection>

            <FieldSection title="暴伤" badge={formatCompactNumber(criticalMultiplier)} badgeLabel="最终倍率" tone="gold">
              <div className="fixed-value">
                <span>基础暴伤</span><strong>1.3</strong><small>游戏固定值</small>
              </div>
              <ExpressionInput
                label="额外暴伤加成"
                value={fields.extraCriticalDamage}
                onChange={(value) => setField("extraCriticalDamage", value)}
                percentage
              />
            </FieldSection>
          </section>

          <section className="side-card defense-card" aria-labelledby="defense-title">
            <div className="side-card-title">
              <span>守</span>
              <div><small>DEFENDER</small><h2 id="defense-title">防守方参数</h2></div>
            </div>

            <div className="base-fields defense-base-fields">
              <ExpressionInput
                label="物防 / 法防"
                value={fields.defense}
                onChange={(value) => setField("defense", value)}
                placeholder="输入对应防御数值"
                hint="必填，可输入数值或算式"
              />
            </div>

            <FieldSection title="A类免伤" badge={formatSignedPercent(defenseATotal)} tone="defense">
              {DEFENSE_A_FIELDS.map(([key, label]) => (
                <ExpressionInput
                  key={key}
                  label={label}
                  value={fields[key]}
                  onChange={(value) => setField(key, value)}
                  percentage
                />
              ))}
            </FieldSection>

            <FieldSection title="B类免伤" badge={formatSignedPercent(defenseBTotal)} tone="defense">
              {DEFENSE_B_FIELDS.map(([key, label]) => (
                <ExpressionInput
                  key={key}
                  label={label}
                  value={fields[key]}
                  onChange={(value) => setField(key, value)}
                  percentage
                />
              ))}
            </FieldSection>

            <FieldSection title="D类免伤" badge={formatSignedPercent(valueOf("dDamageReduction"))} tone="defense">
              <ExpressionInput
                label="D类免伤"
                value={fields.dDamageReduction}
                onChange={(value) => setField("dDamageReduction", value)}
                percentage
              />
            </FieldSection>

            <FieldSection title="减暴伤" badge={formatSignedPercent(valueOf("criticalDamageReduction"))} badgeLabel="减免" tone="gold">
              <ExpressionInput
                label="减暴伤"
                value={fields.criticalDamageReduction}
                onChange={(value) => setField("criticalDamageReduction", value)}
                percentage
              />
              <div className="fixed-value total-critical">
                <span>最终暴伤倍率</span><strong>{formatCompactNumber(criticalMultiplier)}</strong><small>基础1.3 + 额外暴伤 − 减暴伤</small>
              </div>
            </FieldSection>
          </section>
        </div>

        <footer className="brand-footer">
          <div className="brand-footer-content">
            <strong>天地劫伤害计算器</strong>
            <span>所有计算均在本机完成</span>
            <a
              className="icp-link"
              href="http://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
            >
              粤ICP备2025406882号
            </a>
          </div>
        </footer>
      </main>

      <section className="result-dock" aria-label="伤害计算结果" aria-live="polite">
        <div className="result-coefficient">
          <small>克制乘区</small>
          <strong>{formatCompactNumber(restraint.effectiveMultiplier)}</strong>
        </div>
        <div className="result-item">
          <span>不暴击</span>
          <strong className={`damage-number ${restraint.tone}${damage ? "" : " waiting"}`}>
            {damage ? formatCompactNumber(damage.nonCritical) : "等待完成公式"}
          </strong>
        </div>
        <div className="result-divider" />
        <div className="result-item">
          <span>暴击</span>
          <strong className={`damage-number critical${damage ? "" : " waiting"}`}>
            {damage ? formatCompactNumber(damage.critical) : "等待完成公式"}
          </strong>
        </div>
      </section>

      {showWarning && (
        <div className="negative-warning" role="alert">
          <span>!</span>
          <p><strong>当前伤害为负数</strong>请确认攻防属性、增减伤和克制系数是否填写正确。</p>
          <button type="button" aria-label="关闭提示" onClick={() => setShowWarning(false)}>×</button>
        </div>
      )}
    </div>
  );
}
